/**
 * Funciones tipadas de los endpoints /onboarding/*.
 *
 * Sprint 9 S9.4b: refactor a shape v7.1 (level 2-tier, days_available
 * con cap MAX_SESSIONS=4 server-side, surface en vocab brief, has_watch
 * boolean, fc_max opcional). Espejo de
 * `backend/app/blueprints/onboarding/routes.py` post-S9.4a.
 *
 * Patrón consistente con `src/api/training.ts`:
 * - GET /onboarding/status → siempre 200, devuelve { completed, profile,
 *   missing_fields }. NO traduce a null porque el shape ya expresa el
 *   estado vacío (profile: null, completed: false).
 * - POST /onboarding → 200 con OnboardingResponse completo (incluye
 *   `sessions_capped` para que el wizard muestre toast si el cap
 *   recortó días). 400 con código de validación se propaga como ApiError.
 */

import { apiFetch } from "./client";
import type {
  OnboardingPayload,
  OnboardingResponse,
  OnboardingStatus,
} from "@/types/api";

/**
 * GET /onboarding/status — estado del onboarding del usuario actual.
 *
 * El frontend lo invoca al cargar /home para decidir si redirigir a
 * /onboarding (cuando completed=false). NO redirige si solo faltan
 * campos opcionales (hr_max es opcional).
 */
export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  return apiFetch<OnboardingStatus>("/onboarding/status");
}

/**
 * POST /onboarding — envía las respuestas del wizard v7.1.
 *
 * Idempotente: si el user ya completó el onboarding, hace update
 * de campos sin error y NO sobrescribe `onboarding_completed_at`.
 *
 * Errores típicos (ApiError con code):
 * - `level_invalid` (no pertenece a [beginner, amateur]).
 * - `days_available_invalid` (fuera de [1, 7] o no entero).
 * - `surface_invalid` (no pertenece a [asfalto, trail, ambos]).
 * - `has_watch_invalid` (no es boolean estricto).
 * - `fc_max_invalid` o `fc_max_out_of_range` (fuera de [120, 220]).
 */
export async function submitOnboarding(
  payload: OnboardingPayload,
): Promise<OnboardingResponse> {
  return apiFetch<OnboardingResponse>("/onboarding", {
    method: "POST",
    body: payload,
  });
}
